import { useCallback, useMemo } from 'react';

import { useUrlPatch } from '@/hooks/urlState';

import { addDays, todayJakarta } from '../format';

/**
 * The "Filter (N)" model of the WMS lists (Shipment-style): each page declares
 * its fields; applied values live in the URL (one param per value, so a
 * filtered list is shareable and survives Back); the panel edits a local
 * draft and Apply / Reset write every field in ONE URL patch that also drops
 * `page`. `api` is what the list, its tab counts and its tiles send.
 */

/** Past-looking chips (created / received) and future-looking ones (ETA, expiry). */
export type DatePresetSet = 'past' | 'future';
export type DatePreset =
  'today' | 'yesterday' | 'last7' | 'last30' | 'tomorrow' | 'next7' | 'next30';

export const DATE_PRESETS: Record<
  DatePresetSet,
  { key: DatePreset; label: string }[]
> = {
  past: [
    { key: 'today', label: 'Hari ini' },
    { key: 'yesterday', label: 'Kemarin' },
    { key: 'last7', label: '7 hari' },
    { key: 'last30', label: '30 hari' },
  ],
  future: [
    { key: 'today', label: 'Hari ini' },
    { key: 'tomorrow', label: 'Besok' },
    { key: 'next7', label: '7 hari ke depan' },
    { key: 'next30', label: '30 hari ke depan' },
  ],
};

const PRESET_KEYS = new Set<string>(
  Object.values(DATE_PRESETS).flatMap((list) => list.map((p) => p.key))
);

/**
 * A preset as an inclusive `YYYY-MM-DD` range in WIB calendar days — the
 * same days the backend filters on.
 */
export const resolvePreset = (
  preset: DatePreset,
  today = todayJakarta()
): { from: string; to: string } => {
  switch (preset) {
    case 'yesterday': {
      const d = addDays(today, -1);
      return { from: d, to: d };
    }
    case 'last7':
      return { from: addDays(today, -6), to: today };
    case 'last30':
      return { from: addDays(today, -29), to: today };
    case 'tomorrow': {
      const d = addDays(today, 1);
      return { from: d, to: d };
    }
    case 'next7':
      return { from: today, to: addDays(today, 6) };
    case 'next30':
      return { from: today, to: addDays(today, 29) };
    default:
      return { from: today, to: today };
  }
};

export interface FilterOption {
  value: string;
  label: string;
}

export type FilterField =
  /** URL `clientID` (+ `clientName` for the label). */
  | { kind: 'client'; label: string }
  /** URL `vendorID` (+ `vendorName`); inactive vendors included. */
  | { kind: 'vendor'; label: string }
  /** A hub (one of the WMS hub list); URL `key` = API param, value the hub id. */
  | { kind: 'hub'; key: string; label: string }
  /** Chips; URL `key` = API param. */
  | { kind: 'enum'; key: string; label: string; options: FilterOption[] }
  /**
   * Presets XOR a manual range. URL `${key}Preset` / `${key}From` /
   * `${key}To`; API `${key}From` / `${key}To` (e.g. key `date` → dateFrom).
   */
  | { kind: 'date'; key: string; label: string; presets?: DatePresetSet }
  /** Free text (ILIKE on the server); URL `key` = API param. */
  | { kind: 'text'; key: string; label: string; placeholder?: string }
  /** CSV upload batch: URL `uploadID` (+ `uploadCode`). */
  | { kind: 'upload'; label: string }
  /** Destination city (core master): URL `destinationCityID` (+
   *  `destinationCityName`); `none` = still unresolved. */
  | { kind: 'city'; label: string }
  /**
   * Location → Section → Bin: URL `locationID` / `sectionID` / `binID`;
   * the deepest chosen one goes out as `storageID`.
   */
  | { kind: 'storage'; label: string };

/** Flat URL-param → value map (draft and applied share it). */
export type FilterValues = Readonly<Record<string, string>>;

/** The URL params one field owns; the first ones count it as active. */
export const keysOf = (
  f: FilterField
): { active: string[]; label: string[] } => {
  switch (f.kind) {
    case 'client':
      return { active: ['clientID'], label: ['clientName'] };
    case 'vendor':
      return { active: ['vendorID'], label: ['vendorName'] };
    case 'upload':
      return { active: ['uploadID'], label: ['uploadCode'] };
    case 'city':
      return {
        active: ['destinationCityID'],
        label: ['destinationCityName'],
      };
    case 'date':
      return {
        active: [`${f.key}Preset`, `${f.key}From`, `${f.key}To`],
        label: [],
      };
    case 'storage':
      return { active: ['locationID', 'sectionID', 'binID'], label: [] };
    default:
      return { active: [f.key], label: [] };
  }
};

export const filterParamKeys = (fields: readonly FilterField[]): string[] =>
  fields.flatMap((f) => {
    const k = keysOf(f);
    return [...k.active, ...k.label];
  });

export const emptyFilters = (fields: readonly FilterField[]): FilterValues =>
  Object.fromEntries(filterParamKeys(fields).map((k) => [k, '']));

/** A date / storage chain counts once, whichever of its params is set. */
export const countActiveFilters = (
  fields: readonly FilterField[],
  v: FilterValues
): number =>
  fields.filter((f) => keysOf(f).active.some((k) => (v[k] ?? '').trim()))
    .length;

/** Read the applied values from the URL (unknown presets dropped). */
export const readFilters = (
  fields: readonly FilterField[],
  params: URLSearchParams
): FilterValues => {
  const out: Record<string, string> = {};
  fields.forEach((f) => {
    const { active, label } = keysOf(f);
    [...active, ...label].forEach((k) => {
      out[k] = params.get(k) ?? '';
    });
    if (f.kind === 'date') {
      const p = `${f.key}Preset`;
      if (out[p] && !PRESET_KEYS.has(out[p])) out[p] = '';
      // A preset wins over a hand-edited range (they never coexist).
      if (out[p]) {
        out[`${f.key}From`] = '';
        out[`${f.key}To`] = '';
      }
    }
    if (f.kind === 'storage') {
      // A child without its parent is not a chain.
      if (!out.locationID) out.sectionID = '';
      if (!out.sectionID) out.binID = '';
    }
    if (f.kind === 'enum' && out[f.key]) {
      if (!f.options.some((o) => o.value === out[f.key])) out[f.key] = '';
    }
  });
  return out;
};

/**
 * Manual ranges that end before they start, by field key (the backend
 * rejects them with a 422 on `${key}From`).
 */
export const invalidRanges = (
  fields: readonly FilterField[],
  v: FilterValues
): string[] =>
  fields
    .filter(
      (f): f is Extract<FilterField, { kind: 'date' }> => f.kind === 'date'
    )
    .filter((f) => {
      const from = v[`${f.key}From`];
      const to = v[`${f.key}To`];
      return !!from && !!to && from > to;
    })
    .map((f) => f.key);

type ApiValue = string | number;

/** What the list / summaries send for the applied values. */
export const toApiParams = (
  fields: readonly FilterField[],
  v: FilterValues,
  today?: string
): Record<string, ApiValue> => {
  const out: Record<string, ApiValue> = {};
  const num = (s: string | undefined) => {
    const n = Number(s);
    return s && Number.isFinite(n) ? n : null;
  };
  fields.forEach((f) => {
    switch (f.kind) {
      case 'client': {
        const id = num(v.clientID);
        if (id !== null) out.clientID = id;
        break;
      }
      case 'vendor':
        if (v.vendorID) out.vendorID = v.vendorID;
        break;
      case 'hub': {
        const id = num(v[f.key]);
        if (id !== null) out[f.key] = id;
        break;
      }
      case 'upload':
        if (v.uploadID) out.uploadID = v.uploadID;
        break;
      case 'city':
        if (v.destinationCityID) out.destinationCityID = v.destinationCityID;
        break;
      case 'date': {
        const preset = v[`${f.key}Preset`];
        const range = preset
          ? resolvePreset(preset as DatePreset, today)
          : { from: v[`${f.key}From`] ?? '', to: v[`${f.key}To`] ?? '' };
        if (range.from) out[`${f.key}From`] = range.from;
        if (range.to) out[`${f.key}To`] = range.to;
        break;
      }
      case 'storage': {
        const id = v.binID || v.sectionID || v.locationID;
        if (id) out.storageID = id;
        break;
      }
      case 'text': {
        const t = (v[f.key] ?? '').trim();
        if (t) out[f.key] = t;
        break;
      }
      default:
        if (v[f.key]) out[f.key] = v[f.key];
    }
  });
  return out;
};

export interface ListFilters {
  fields: readonly FilterField[];
  /** Applied (URL) values. */
  values: FilterValues;
  /** Applied filters as API params — spread into list + summary queries. */
  api: Record<string, ApiValue>;
  /** Stable dependency key of `api` (for useLoad deps). */
  apiKey: string;
  /** "Filter (N)". */
  count: number;
  /** Write every field (Apply / Reset) in one URL patch; drops `page`. */
  write: (next: FilterValues) => void;
  empty: FilterValues;
}

/** URL-backed applied filters of a list page. Pass a module-level `fields`. */
export function useListFilters(fields: readonly FilterField[]): ListFilters {
  const { searchParams, patch } = useUrlPatch();
  const raw = searchParams.toString();
  const values = useMemo(
    () => readFilters(fields, new URLSearchParams(raw)),
    [fields, raw]
  );
  const api = useMemo(() => toApiParams(fields, values), [fields, values]);
  const apiKey = JSON.stringify(api);
  const empty = useMemo(() => emptyFilters(fields), [fields]);

  const write = useCallback(
    (next: FilterValues) =>
      patch((params) => {
        filterParamKeys(fields).forEach((k) => {
          const val = (next[k] ?? '').trim();
          if (val) params.set(k, val);
          else params.delete(k);
        });
        params.delete('page');
      }),
    [fields, patch]
  );

  return useMemo(
    () => ({
      fields,
      values,
      api,
      apiKey,
      count: countActiveFilters(fields, values),
      write,
      empty,
    }),
    [fields, values, api, apiKey, write, empty]
  );
}
